import axios from 'axios';

import config from '../config';

const secretKey = process.env.REACT_APP_SECRET_KEY ?? '';
const apiKey = process.env.REACT_APP_API_KEY ?? '';

const foodBusinessClient = axios.create({
  baseURL: config.apiUrl,
  timeout: 50000,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
    'HOMECOOK-SECRET-KEY': secretKey,
    'HOMECOOK-API-KEY': apiKey,
  },
});

export type FoodBusinessHearAboutSource =
  | 'word_of_mouth'
  | 'online_search'
  | 'social_media'
  | 'online_ads_print'
  | 'event_trade_show'
  | 'email_marketing'
  | 'blog_article'
  | 'dont_remember';

export type FoodBusinessFormSource = 'food_business_form' | 'pawdit_demo';

export type FoodBusinessSubmitRequest = {
  first_name: string;
  last_name?: string;
  email: string;
  business_name: string;
  business_website?: string;
  business_zip_code?: string;
  hear_about_source?: FoodBusinessHearAboutSource | '';
  notes?: string;
  form_source?: FoodBusinessFormSource;
  web_env?: string;
};

/** Split a visitor's full name into CRM first/last fields. */
export const splitFullName = (
  fullName: string
): { first_name: string; last_name: string } => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
};

/** Submit the landing-page community or Pawdit demo form. */
export const submitFoodBusinessForm = async (
  payload: FoodBusinessSubmitRequest
): Promise<{ ok: boolean } | null> => {
  const response = await foodBusinessClient.post('/public/food-business/submit', {
    ...payload,
    form_source: payload.form_source ?? 'food_business_form',
    web_env: payload.web_env ?? process.env.REACT_APP_NODE_ENV,
  });
  const data = response.data?.body ?? response.data;
  return data ?? null;
};

/** Maps axios failures to user-facing copy for landing lead forms. */
export const getFoodBusinessSubmitErrorMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) {
    return 'Something went wrong. Please try again.';
  }
  const status = error.response?.status;
  const serverMessage =
    (error.response?.data as { error?: string; detail?: string } | undefined)?.error ??
    (error.response?.data as { detail?: string } | undefined)?.detail;

  if (status === 429) {
    return 'Too many attempts. Please wait a bit and try again.';
  }
  if (status === 400) {
    return serverMessage ?? 'Please check your entries and try again.';
  }
  if (!error.response) {
    return 'Network error. Check your connection and try again.';
  }
  return serverMessage ?? 'Could not complete signup. Please try again.';
};
